import { useInfiniteQuery } from 'react-query';
import _ from 'lodash';

import {
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
  infiniteEmptyResults,
} from 'data_sources/constants';

import type { User } from 'views/App/Portal/LexID/Users/_types/user';
import type { ApiCollectionResponse } from 'types/api_response';

export const getUsers = async <T extends User>(
  organizationId: string,
  params = {},
  onError?: (errors: ServerErrorResponse) => void
) => {
  try {
    const paramString = createParamString(params);
    const data = await fetcher({
      method: 'get',
      url: `${baseApiUrl()}/lexid/admin-user/v1/organizations/${organizationId}/users?${paramString}`,
    });
    return data.content || [];
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function useOrganizationUsers<T extends User>(
  organizationId: string,
  params = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexid/admin-user/v1/organizations/${organizationId}/users`;

  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['organization_users', organizationId, params],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString(params);
        const data: ApiCollectionResponse<T> = await fetcher({
          method: 'get',
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}
